import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import API_BASE from "./apiBase";

/**
 * TRACK - the public page a customer opens from a "your parcel is on its way"
 * email. No sign-in: the 32-character key in the URL (#/track/<key>) is the
 * whole secret and the server answers with that one parcel only.
 *
 * Shows the parcel's state in plain words, and while it is on its way, a map
 * with the driver's last reported position and the customer's stop, plus the
 * office's own ETA. Refreshes itself every 30 seconds while live.
 */
const S = {
  page: { minHeight: "100vh", background: "#f8fafc", fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif" },
  wrap: { maxWidth: 720, margin: "0 auto", padding: "18px 14px" },
  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 12 },
  h: { fontSize: 20, fontWeight: 800, color: "#111827", margin: 0 },
  status: { fontSize: 16, fontWeight: 700, color: "#1976d2", marginTop: 6 },
  muted: { fontSize: 13, color: "#6b7280" },
  eta: { fontSize: 28, fontWeight: 900, color: "#1976d2" },
};
const pin = (colour) => L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50%;background:${colour};border:3px solid #fff;box-shadow:0 0 0 2px ${colour}"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

export default function TrackPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let timer = null; let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/track/${encodeURIComponent(token)}`);
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d?.error?.message || "This tracking link is not valid");
        if (!cancelled) { setData(d); setError(null); timer = setTimeout(load, (d.refreshSeconds || 60) * 1000); }
      } catch (e) {
        if (!cancelled) { setError(e.message); timer = setTimeout(load, 60000); }
      }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [token]);

  const fmtWhen = (v) => (v ? new Date(v).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : null);

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.card}>
          {error && <div style={{ color: "#b91c1c" }}>{error}</div>}
          {!error && !data && <div style={S.muted}>Loading…</div>}
          {data && (
            <>
              <h1 style={S.h}>{data.customerFirstName ? `${data.customerFirstName}, your parcel` : "Your parcel"}</h1>
              <div style={S.status}>{data.statusText}</div>
              <div style={S.muted}>From {data.branch}{data.driverFirstName && data.live ? ` · with ${data.driverFirstName}` : ""}</div>
              {data.live && data.eta && data.eta.minutes !== null && (
                <div style={{ marginTop: 12 }}>
                  <div style={S.eta}>{data.eta.minutes <= 1 ? "Arriving now" : `About ${data.eta.minutes} min`}</div>
                  <div style={S.muted}>
                    {data.eta.at ? `Expected around ${fmtWhen(data.eta.at)}` : ""}
                    {typeof data.eta.dropsAway === "number" && data.eta.dropsAway > 0
                      ? ` · ${data.eta.dropsAway} stop${data.eta.dropsAway === 1 ? "" : "s"} before yours` : ""}
                  </div>
                </div>
              )}
              {data.live && (!data.eta || data.eta.minutes === null) && (
                <div style={{ ...S.muted, marginTop: 10 }}>The driver is out with your parcel. An arrival time will appear once their position is known.</div>
              )}
              {data.deliveredAt && <div style={{ ...S.muted, marginTop: 8 }}>Delivered at {fmtWhen(data.deliveredAt)}.</div>}
            </>
          )}
        </div>
        {data && data.live && (data.driver || data.customer) && (
          <div style={{ ...S.card, padding: 0, overflow: "hidden", height: 380 }}>
            <MapContainer
              center={[(data.driver || data.customer).lat, (data.driver || data.customer).lng]}
              zoom={13} style={{ height: "100%", width: "100%" }}>
              <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {data.driver && (
                <Marker position={[data.driver.lat, data.driver.lng]} icon={pin("#1976d2")}>
                  <Popup>{data.driverFirstName || "Driver"}{data.driver.at ? ` · seen ${fmtWhen(data.driver.at)}` : ""}</Popup>
                </Marker>
              )}
              {data.customer && (
                <Marker position={[data.customer.lat, data.customer.lng]} icon={pin("#b45309")}>
                  <Popup>Your address</Popup>
                </Marker>
              )}
              {data.driver && data.customer && (
                <Polyline positions={[[data.driver.lat, data.driver.lng], [data.customer.lat, data.customer.lng]]}
                  pathOptions={{ color: "#1976d2", dashArray: "6 6", weight: 2 }} />
              )}
            </MapContainer>
          </div>
        )}
        <div style={{ ...S.muted, textAlign: "center" }}>This page updates itself. Positions are approximate.</div>
      </div>
    </div>
  );
}
